import { spacing, TextBadge, Tooltip } from '@scality/core-ui';
import type React from 'react';
import styled from 'styled-components';
import { useInstanceLabel } from '../hooks/portal/useInstanceLabel';

type InstanceBadgeProps = {
  id: string;
};

// A wrapped badge breaks into two ragged boxes, so it truncates to its container instead.
const OneLineBadge = styled(TextBadge)`
  display: inline-block;
  /* Border box minus its own horizontal margins: max-width counts neither, and either one left
     over pushes the rounded edge past the clip. */
  box-sizing: border-box;
  max-width: calc(100% - ${spacing.r8});
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`;

// `Tooltip` wraps the badge in two elements of its own, which shrink-wrap to the whole label and
// leave `max-width: 100%` nothing to bite on.
const Clamp = styled.span`
  display: inline-block;
  max-width: 100%;
  min-width: 0;

  > *,
  > * > * {
    max-width: 100%;
  }
`;

/**
 * An instance id rendered the way the deployment selector renders it: `account - name`,
 * degrading to the raw id while unresolved or on a portal failure. The tooltip always
 * carries the raw id, which is what a support engineer needs to paste elsewhere.
 */
export const InstanceBadge: React.FC<InstanceBadgeProps> = ({ id }) => {
  const { label } = useInstanceLabel(id);

  return (
    <Clamp>
      <Tooltip overlay={label === id ? id : `${label} — ${id}`}>
        <OneLineBadge text={label} variant="infoPrimary" />
      </Tooltip>
    </Clamp>
  );
};
